import { useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { useSeoMeta } from '@unhead/react';
import { nip19 } from 'nostr-tools';

import { PuzzleEditor } from '@/components/editor/PuzzleEditor';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePublishPuzzle, usePuzzleByNaddr } from '@/hooks/usePuzzles';
import { decodePuzzle, PUZZLE_KIND, type PuzzleData } from '@/lib/puzzle';
import { LoginArea } from '@/components/auth/LoginArea';

export default function EditorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const paramP = searchParams.get('p');
  const paramNaddr = searchParams.get('naddr');
  const { toast } = useToast();
  const { user } = useCurrentUser();
  const { mutateAsync: publishPuzzle, isPending } = usePublishPuzzle();
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [pendingPuzzle, setPendingPuzzle] = useState<PuzzleData | null>(null);
  const [dTag, setDTag] = useState('');

  // ── Load puzzle from ?p= URL param ─────────────────────────────────────────
  const urlPuzzle = (() => {
    if (!paramP) return undefined;
    try {
      return decodePuzzle(paramP);
    } catch {
      return undefined;
    }
  })();

  // ── Load puzzle from ?naddr= for editing ───────────────────────────────────
  const naddrQuery = usePuzzleByNaddr(paramNaddr ?? undefined);

  useSeoMeta({
    title: 'Editor — Somaria Tiles',
    description: 'Create and share Hamilton Path tile puzzles on Nostr.',
  });

  const handlePublish = (puzzle: PuzzleData) => {
    setPendingPuzzle(puzzle);

    // If editing an existing puzzle, pre-fill its d-tag.
    if (paramNaddr && naddrQuery.data) {
      setDTag(naddrQuery.data.dTag);
    } else {
      // Generate a default d-tag from the title — no length limit.
      const slug = puzzle.title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
      setDTag(slug || `puzzle-${Date.now().toString(36)}`);
    }
    setPublishDialogOpen(true);
  };

  const confirmPublish = async () => {
    if (!pendingPuzzle || !dTag.trim()) return;

    try {
      const event = await publishPuzzle({ puzzle: pendingPuzzle, dTag: dTag.trim() });
      // Encode a proper naddr from the signed event so the Play page can
      // decode it and query relays for this specific puzzle.
      const naddr = nip19.naddrEncode({
        kind: PUZZLE_KIND,
        pubkey: event.pubkey,
        identifier: dTag.trim(),
        relays: [],
      });

      toast({
        title: paramNaddr ? 'Puzzle updated!' : 'Puzzle published!',
        description: paramNaddr
          ? 'Your puzzle has been updated on Nostr.'
          : 'Your puzzle is now discoverable on Nostr.',
      });
      setPublishDialogOpen(false);
      setPendingPuzzle(null);
      // Navigate to the play page using the proper naddr identifier.
      navigate(`/play/${naddr}`);
    } catch (err) {
      toast({
        title: 'Failed to publish',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  // ── Loading state for naddr edit ──────────────────────────────────────────
  if (paramNaddr && naddrQuery.isLoading) {
    return (
      <div className="min-h-screen bg-background py-8 px-4">
        <div className="max-w-3xl mx-auto space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-20 w-full" />
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </div>
    );
  }

  // If editing via naddr but puzzle not found, show error.
  if (paramNaddr && !naddrQuery.isLoading && !naddrQuery.data) {
    return (
      <div className="min-h-screen bg-background py-8 px-4 flex items-center justify-center">
        <Card className="border-dashed max-w-md">
          <CardContent className="py-12 px-8 text-center">
            <p className="text-muted-foreground">
              Could not load this puzzle for editing. It may have been deleted or the relays are unavailable.
            </p>
            <Button asChild className="mt-4">
              <Link to="/editor">New Puzzle</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const initialPuzzle = naddrQuery.data?.puzzle ?? urlPuzzle;

  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold">
              {paramNaddr ? 'Edit Puzzle' : 'Puzzle Editor'}
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Design custom tile puzzles and share them on Nostr.
            </p>
          </div>
          <Button asChild variant="outline" size="sm">
            <Link to="/browse">Browse</Link>
          </Button>
        </div>

        <PuzzleEditor
          key={paramNaddr ?? 'new'}
          initialPuzzle={initialPuzzle}
          onPublish={handlePublish}
        />

        {/* Publish dialog */}
        {publishDialogOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
            <Card className="w-full max-w-md">
              <CardHeader>
                <CardTitle>{paramNaddr ? 'Update Puzzle' : 'Publish to Nostr'}</CardTitle>
                <CardDescription>
                  {paramNaddr
                    ? 'Re-publish your puzzle with the same ID to update it.'
                    : 'Share your puzzle so others can discover and play it.'}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {!user ? (
                  <div className="space-y-3">
                    <p className="text-sm text-muted-foreground">
                      You need to be logged in to publish puzzles.
                    </p>
                    <LoginArea className="w-full" />
                  </div>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="d-tag">Puzzle ID (d-tag)</Label>
                      <Input
                        id="d-tag"
                        value={dTag}
                        onChange={(e) => setDTag(e.target.value)}
                        placeholder="my-awesome-puzzle"
                        className="mt-1"
                        // Lock the d-tag when editing so it updates the same puzzle.
                        disabled={!!paramNaddr}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        {paramNaddr
                          ? 'Locked — updating the existing puzzle.'
                          : 'A unique identifier for your puzzle. Use the same ID to update it later.'}
                      </p>
                    </div>
                    <div className="flex gap-2 justify-end">
                      <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>
                        Cancel
                      </Button>
                      <Button onClick={confirmPublish} disabled={isPending || !dTag.trim()}>
                        {isPending ? 'Publishing...' : paramNaddr ? 'Update' : 'Publish'}
                      </Button>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}
